import React, { useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Select from 'components/Common/Form/Input/Select'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import TextInput from 'components/Common/Form/Input/TextInput'
import { formToObject } from 'lib/forms/formToObject'
import { connect } from 'react-redux'
import TextArea from 'components/Common/Form/Input/TextArea'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import { sendEnquiryRequest } from 'api/notification'
import { getSupportEmailForCurrentRegion } from 'selectors/supportContactSelector'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import { arrayToObject } from 'lib/array/arrayUtils'

type IssueOption = 'purchase' | 'purchased' | 'somethingElse'

const Root = styled(Pane)`
  margin-top: ${rem(50)};

  > * + * {
    margin-top: ${rem(24)};
  }
`

const Form = styled.form`
  > * + * {
    margin-top: ${rem(12)};
  }
`

const EnquiryInput = styled(TextArea)`
  resize: vertical;
`

// TEXT TODO: confirm with design
const TextAreaMinimumText = styled(Caption)`
  color: ${props => props.theme.palette.neutral.default.four};
  margin-top: ${rem(-20)};
`

interface ContactForm {
  issue: IssueOption
  subIssue?: string
  email: string
  name: string
  phone: string
  enquiry: string
  orderId?: string
}

interface Props {
  email: string
  regionCode: string
}

function ContactUsForm({ email, regionCode }: Props) {
  const intl = useIntl()
  const [issue, setIssue] = useState<{ option: IssueOption, text: string }>()
  const [sending, setSending] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(false)

  const issuesDataArray: Array<{ option: IssueOption, text: string }> = [
    {
      option: 'purchase',
      text: intl.formatMessage({
        id: 'th2oN6h2L7',
        defaultMessage: 'I have a question about a purchase I want to make',
        description: 'Contact form issue option for pre-purchase questions',
      }),
    },
    {
      option: 'purchased',
      text: intl.formatMessage({
        id: '1EpawRPc3e',
        defaultMessage: 'I have a question about a purchase I made on Luxury Escapes',
        description: 'Contact form issue option for post-purchase questions',
      }),
    },
    {
      option: 'somethingElse',
      text: intl.formatMessage({
        id: 'p0u5xT5oni',
        defaultMessage: 'I have a question about something else',
        description: 'Contact form issue option for other questions',
      }),
    },
  ]

  const issuesData = arrayToObject(issuesDataArray, i => i.option)

  const subIssuesData: Record<IssueOption, Array<{ option: string, text: string }>> = {
    purchase: [
      {
        option: 'bookingAssistance',
        text: intl.formatMessage({
          id: 'BRm3Wpds3x',
          defaultMessage: 'Booking Assistance',
          description: 'Contact form sub-issue option for booking assistance',
        }),
      },
      {
        option: 'quoteRequest',
        text: intl.formatMessage({
          id: 'ICh6K0gsvW',
          defaultMessage: 'Quote Request',
          description: 'Contact form sub-issue option for quote request',
        }),
      },
    ],
    purchased: [
      {
        option: 'changeBooking',
        text: intl.formatMessage({
          id: 'PgGwPDytl9',
          defaultMessage: 'Change Booking',
          description: 'Contact form sub-issue option for changing a booking',
        }),
      },
      {
        option: 'cancelBooking',
        text: intl.formatMessage({
          id: '+wMoKb6T6M',
          defaultMessage: 'Cancel Booking',
          description: 'Contact form sub-issue option for cancelling a booking',
        }),
      },
      {
        option: 'generalEnquiries',
        text: intl.formatMessage({
          id: 'U832ljGDjI',
          defaultMessage: 'General Enquiries',
          description: 'Contact form sub-issue option for general enquiries',
        }),
      },
      {
        option: 'complaint',
        text: intl.formatMessage({
          id: 'Xo+reDjU+r',
          defaultMessage: 'Complaint',
          description: 'Contact form sub-issue option for complaints',
        }),
      },
      {
        option: 'positiveFeedback',
        text: intl.formatMessage({
          id: 'hiUy6m/B4L',
          defaultMessage: 'Positive Feedback',
          description: 'Contact form sub-issue option for positive feedback',
        }),
      },
    ],
    somethingElse: [
      {
        option: 'changeDetails',
        text: intl.formatMessage({
          id: '+hmftm9mSp',
          defaultMessage: 'I want to change my details (name, contact number, email address)',
          description: 'Contact form sub-issue option for changing account details',
        }),
      },
      {
        option: 'forgotPassword',
        text: intl.formatMessage({
          id: 'CQS+4xkESb',
          defaultMessage: 'I have forgotten my password',
          description: 'Contact form sub-issue option for forgotten password',
        }),
      },
      {
        option: 'leCredits',
        text: intl.formatMessage({
          id: '29beTf1ZOO',
          defaultMessage: 'I have a question about my Luxury Escapes credits',
          description: 'Contact form sub-issue option for Luxury Escapes credits question',
        }),
      },
      {
        option: 'unsubscribe',
        text: intl.formatMessage({
          id: 'Aymdm5ZjfW',
          defaultMessage: 'I want to unsubscribe from Luxury Escapes emails',
          description: 'Contact form sub-issue option for email unsubscription',
        }),
      },
      {
        option: 'changeSubscription',
        text: intl.formatMessage({
          id: 'hx/LFP+Slm',
          defaultMessage: 'I want to change my email subscription frequency',
          description: 'Contact form sub-issue option for changing email subscription frequency',
        }),
      },
      {
        option: 'otherQuestion',
        text: intl.formatMessage({
          id: '2w3V0NDNQs',
          defaultMessage: 'I have a question that you guys have not thought of yet!',
          description: 'Contact form sub-issue option for other questions not listed',
        }),
      },
    ],
  }

  const onFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const values = formToObject<ContactForm>(e.currentTarget)
    const formObject = {
      ...values,
      issue: issuesData[values.issue],
      subIssue: subIssuesData[values.issue].find(i => i.option === values.subIssue),
    }

    setSending(true)
    setError(false)
    sendEnquiryRequest(formObject, regionCode)
      .then(() => setSuccess(true))
      .catch(() => setError(true))
      .finally(() => setSending(false))
  }

  const onIssueSelect = (e: React.FormEvent<HTMLSelectElement>) => {
    const selectedIssue = issuesData[e.currentTarget.value as IssueOption]
    setIssue(selectedIssue)
  }

  const subIssues = issue ? subIssuesData[issue.option] : undefined

  const submittingLabel = intl.formatMessage({
    id: 'usFHmBgHPZ',
    defaultMessage: 'Submitting...',
    description: 'Contact form submit button text while form is being submitted',
  })
  const thankYouLabel = intl.formatMessage({
    id: 'NzJl2qsylf',
    defaultMessage: 'Thank you',
    description: 'Contact form submit button text after successful submission',
  })
  const submitLabel = intl.formatMessage({
    id: 'FJZWJ8jIx/',
    defaultMessage: 'Submit',
    description: 'Contact form submit button text',
  })

  return (
    <Root id="contact-form" background="neutral-six">
      <Heading variant="heading3">
        {intl.formatMessage({
          id: '4BSPX1rux8',
          defaultMessage: 'Can\u2019t find what you\u2019re looking for? Submit an enquiry',
          description: 'Contact form heading prompting users to submit an enquiry',
        })}
      </Heading>
      {success && <MessageBanner kind="success">
        {intl.formatMessage({
          id: 'aIBtI1GnWc',
          defaultMessage: 'Thank you! Your enquiry has been submitted successfully. A member of our team will be in touch to assist you.',
          description: 'Success message after contact form enquiry is submitted',
        })}
      </MessageBanner>}
      <Form name="contactUs" onSubmit={onFormSubmit}>
        <div>
          <Select
            name="issue"
            label={intl.formatMessage({
              id: 'U/3orEDs6I',
              defaultMessage: 'What can we do to help today?',
              description: 'Contact form issue selector label',
            })}
            placeholder={intl.formatMessage({
              id: 'RNZNXw2fJV',
              defaultMessage: 'Please select your issue',
              description: 'Contact form issue selector placeholder',
            })}
            onChange={onIssueSelect}
            requiredErrorMessage={intl.formatMessage({
              id: 'DxNz//jUF0',
              defaultMessage: 'Please select an issue',
              description: 'Contact form issue selector validation error when no issue is selected',
            })}
            required
          >
            {issuesDataArray.map((selection) =>
              <option key={selection.option} value={selection.option}>
                {selection.text}
              </option>,
            )}
          </Select>
          {subIssues && issue &&
            <Select
              label={issue.text}
              name="subIssue"
              placeholder={intl.formatMessage({
                id: 'jlLc59CMap',
                defaultMessage: 'Please select an answer',
                description: 'Contact form sub-issue selector placeholder',
              })}
              required
              requiredErrorMessage={intl.formatMessage({
                id: 'Goieh3iXvS',
                defaultMessage: 'Please select an option for your issue',
                description: 'Contact form sub-issue selector validation error when no option is selected',
              })}
            >
              {subIssues.map((selection) =>
                <option
                  key={selection.option}
                  value={selection.option}
                >
                  {selection.text}
                </option>,
              )}
            </Select>}
          <TextInput
            label={intl.formatMessage({
              id: 'rToLTTQ4nX',
              defaultMessage: 'Email Address',
              description: 'Contact form email address field label',
            })}
            type="email"
            name="email"
            required
            invalidErrorMessage={intl.formatMessage({
              id: '6As3cAh3K6',
              defaultMessage: 'Please enter a valid email address',
              description: 'Contact form email field validation error for invalid email',
            })}
          />
          <Group direction="horizontal" horizontalAlign="stretch" gap={16}>
            <TextInput
              label={intl.formatMessage({
                id: 'uVZBZEtPIS',
                defaultMessage: 'Name',
                description: 'Contact form name field label',
              })}
              type="name"
              name="name"
              required
              invalidErrorMessage={intl.formatMessage({
                id: '/86qzVjbrG',
                defaultMessage: 'Please enter your name',
                description: 'Contact form name field validation error when name is invalid',
              })}
            />
            <TextInput
              required
              type="tel"
              name="phone"
              label={intl.formatMessage({
                id: 'PiH6fg7tPW',
                defaultMessage: 'Phone',
                description: 'Contact form phone number field label',
              })}
              invalidErrorMessage={intl.formatMessage({
                id: 'uMoDmk1UWv',
                defaultMessage: 'Please enter a valid phone number',
                description: 'Contact form phone field validation error for invalid phone number',
              })}
            />
          </Group>
          {issue?.option === 'purchased' &&
            <TextInput
              type="text"
              name="orderId"
              pattern="[a-zA-Z0-9]{6}"
              maxLength={6}
              minLength={6}
              label={intl.formatMessage({
                id: 'yrGqzG5sOL',
                defaultMessage: 'Order Number',
                description: 'Contact form order number field label',
              })}
              invalidErrorMessage={intl.formatMessage({
                id: 'b6TXCZh0vg',
                defaultMessage: 'Please enter a valid order number',
                description: 'Contact form order number field validation error for invalid order number',
              })}
            />}
          <EnquiryInput
            label={intl.formatMessage({
              id: '9lQdJ06oiC',
              defaultMessage: 'Please provide more details below - including accommodation, tour or booking information',
              description: 'Contact form enquiry details text area label',
            })}
            required
            name="enquiry"
            minLength={30}
            cols={5}
            invalidErrorMessage={intl.formatMessage({
              id: 'aRVhlvrfL8',
              defaultMessage: 'Please provide at least 30 characters to describe your issue',
              description: 'Contact form enquiry text area validation error for minimum character count',
            })}
          />
          <TextAreaMinimumText variant="medium" align="end">
            {intl.formatMessage({
              id: 'scKCHF10lO',
              defaultMessage: 'minimum of 30 characters',
              description: 'Contact form enquiry text area minimum character count hint',
            })}
          </TextAreaMinimumText>
        </div>
        {error && <MessageBanner
          kind="critical"
          heading={intl.formatMessage({
            id: '3JpnpT6fg+',
            defaultMessage: 'Oops, looks like there has been an error submitting your enquiry.',
            description: 'Contact form error banner heading when submission fails',
          })}
        >
          <FormattedMessage
            id="u5k0qPfHwk"
            defaultMessage="If you continue to experience issues, please try again later or send us an email at <emailLink>{supportEmail}</emailLink>."
            description="Contact form error message with support email link when submission fails"
            values={{
              supportEmail: email,
              emailLink: (chunks: React.ReactNode) => <TextLink href={'mailto:' + email}>{chunks}</TextLink>,
            }}
          />
        </MessageBanner>}
        <TextButton
          kind="primary"
          disabled={sending || success}
          fit="flex"
          size="large"
          type="submit"
        >
          {sending && submittingLabel}
          {!sending && (success ? thankYouLabel : submitLabel)}
        </TextButton>
      </Form>
    </Root>
  )
}

function mapStateToProps(state: App.State) {
  return {
    email: getSupportEmailForCurrentRegion(state),
    regionCode: state.geo.currentRegionCode,
  }
}

export default connect(mapStateToProps)(ContactUsForm)
